// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  PackageSearch, 
  RefreshCcw, 
  ShoppingCart, 
  Package, 
  Phone,
  Mail,
  MapPin,
  X,
  Trash2,
  Info,
  ChevronDown
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useMediaQuery } from "@/hooks/use-media-query";

interface OrderItem {
  id: string;
  product_name: string;
  variant_size: string;
  variant_color: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface Order {
  id: string;
  created_at: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  shipping_address: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'refunded' | 'failed';
  total: number;
  subtotal: number;
  tps: number;
  tvq: number;
  order_items: OrderItem[];
  order_number: number;
}

const getStatusColor = (status: Order['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
    case 'confirmed':
      return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
    case 'processing':
      return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
    case 'shipped':
      return 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200';
    case 'delivered':
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 hover:bg-red-200';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
  }
};

const getPaymentStatusColor = (status: Order['payment_status']) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
    case 'refunded':
      return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
    case 'failed':
      return 'bg-red-100 text-red-800 hover:bg-red-200';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
  }
};

const getStatusLabel = (status: Order['status']) => {
  switch (status) {
    case 'pending':
      return 'En attente';
    case 'confirmed':
      return 'Confirmée';
    case 'processing':
      return 'En traitement';
    case 'shipped':
      return 'Expédiée';
    case 'delivered':
      return 'Livrée';
    case 'cancelled':
      return 'Annulée';
    default:
      return status;
  }
};

const getPaymentStatusLabel = (status: Order['payment_status']) => {
  switch (status) {
    case 'pending':
      return 'En attente';
    case 'paid':
      return 'Payée';
    case 'refunded':
      return 'Remboursée';
    case 'failed':
      return 'Échec';
    default:
      return status;
  }
};

const OrderMobileCard = ({ 
  order, 
  onSelect, 
  onStatusChange, 
  onPaymentStatusChange, 
  onDeleteClick 
}: { 
  order: Order, 
  onSelect: () => void,
  onStatusChange: (status: Order['status']) => void,
  onPaymentStatusChange: (status: Order['payment_status']) => void,
  onDeleteClick: () => void
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="mb-4 overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-center">
          <div className="font-medium">{order.customer_name}</div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="p-0 h-8 w-8"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          <span className="block mb-1">
            {format(new Date(order.created_at), "d MMMM yyyy", { locale: fr })}
          </span>
          <span className="block">{order.order_number}</span>
        </div>
        
        <div className="flex gap-2 mb-3">
          <Select
            value={order.status}
            onValueChange={(value: Order['status']) => onStatusChange(value)}
          >
            <SelectTrigger className={`h-8 text-xs px-2 border-0 ${getStatusColor(order.status)}`}>
              <SelectValue>{getStatusLabel(order.status)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
              <SelectItem value="confirmed" className="hover:bg-blue-50">Confirmée</SelectItem>
              <SelectItem value="processing" className="hover:bg-purple-50">En traitement</SelectItem>
              <SelectItem value="shipped" className="hover:bg-indigo-50">Expédiée</SelectItem>
              <SelectItem value="delivered" className="hover:bg-green-50">Livrée</SelectItem>
              <SelectItem value="cancelled" className="hover:bg-red-50">Annulée</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={order.payment_status}
            onValueChange={(value: Order['payment_status']) => onPaymentStatusChange(value)}
          >
            <SelectTrigger className={`h-8 text-xs px-2 border-0 ${getPaymentStatusColor(order.payment_status)}`}>
              <SelectValue>{getPaymentStatusLabel(order.payment_status)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
              <SelectItem value="paid" className="hover:bg-green-50">Payée</SelectItem>
              <SelectItem value="refunded" className="hover:bg-orange-50">Remboursée</SelectItem>
              <SelectItem value="failed" className="hover:bg-red-50">Échec</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="font-medium text-right">
          {order.total.toFixed(2)} CAD
        </div>
        
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm mb-1 flex items-center">
              <Mail className="h-3 w-3 mr-2" />
              <a 
                href={`mailto:${order.customer_email}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {order.customer_email}
              </a>
            </p>
            {order.customer_phone && (
              <p className="text-sm mb-1 flex items-center">
                <Phone className="h-3 w-3 mr-2" />
                <a 
                  href={`tel:${order.customer_phone}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {order.customer_phone}
                </a>
              </p>
            )}
            <p className="text-sm mb-3 flex items-start">
              <MapPin className="h-3 w-3 mr-2 mt-1 flex-shrink-0" />
              <span className="flex-1">{order.shipping_address}</span>
            </p>
            
            <div className="flex justify-between mt-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onSelect}
                className="text-xs"
              >
                <Info className="h-3 w-3 mr-1" /> Détails
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-900 hover:bg-red-50 text-xs"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer la commande</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={onDeleteClick}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const AdminOrders = () => {
  const { user } = useAuth();
  const { isAdmin, role, isLoading: isRoleLoading } = useUserRole();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const hasOrderAccess = isAdmin;

  useEffect(() => {
    if (!isRoleLoading && !hasOrderAccess) {
      console.log("User without order access attempted to access orders page");
      navigate("/");
    }
  }, [hasOrderAccess, isRoleLoading, navigate]);

  useEffect(() => {
    if (!user || !hasOrderAccess) return;

    const channel = supabase
      .channel('order-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          console.log('Changement détecté sur la table orders:', payload);
          queryClient.invalidateQueries({ queryKey: ['adminOrders'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, hasOrderAccess, queryClient]);

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['adminOrders'],
    queryFn: async () => {
      console.log("Fetching all orders for admin");
      const { data, error } = await (supabase as any)
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_name,
            variant_size,
            variant_color,
            quantity,
            unit_price,
            total_price
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching orders:", error);
        throw error;
      }

      console.log("Orders fetched successfully:", data);
      return data as Order[];
    },
    enabled: !!user && hasOrderAccess,
  });

  const updateOrderStatus = useMutation({
    mutationFn: async ({ orderId, status }: { orderId: string, status: Order['status'] }) => {
      const { data, error } = await (supabase as any)
        .from('orders')
        .update({ status })
        .eq('id', orderId);

      if (error) throw error;
      return data;
    },
    onMutate: async ({ orderId, status }) => {
      await queryClient.cancelQueries({ queryKey: ['adminOrders'] });
      const previousOrders = queryClient.getQueryData(['adminOrders']) as Order[];
      queryClient.setQueryData(['adminOrders'], (old: Order[] | undefined) => {
        if (!old) return [];
        return old.map(order => {
          if (order.id === orderId) {
            return { ...order, status };
          }
          return order;
        });
      });
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({
          ...selectedOrder,
          status
        });
      }
      return { previousOrders };
    },
    onSuccess: () => {
      toast({
        title: "Statut mis à jour",
        description: "Le statut de la commande a été mis à jour avec succès.",
      });
    },
    onError: (error, variables, context) => {
      console.error('Error updating order status:', error);
      if (context?.previousOrders) {
        queryClient.setQueryData(['adminOrders'], context.previousOrders);
      }
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour du statut.",
        variant: "destructive",
      });
    },
  });

  const updatePaymentStatus = useMutation({
    mutationFn: async ({ orderId, status }: { orderId: string, status: Order['payment_status'] }) => {
      const { data, error } = await (supabase as any)
        .from('orders')
        .update({ payment_status: status })
        .eq('id', orderId);

      if (error) throw error;
      return data;
    },
    onMutate: async ({ orderId, status }) => {
      await queryClient.cancelQueries({ queryKey: ['adminOrders'] });
      const previousOrders = queryClient.getQueryData(['adminOrders']) as Order[];
      queryClient.setQueryData(['adminOrders'], (old: Order[] | undefined) => {
        if (!old) return [];
        return old.map(order => {
          if (order.id === orderId) {
            return { ...order, payment_status: status };
          }
          return order;
        });
      });
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({
          ...selectedOrder,
          payment_status: status
        });
      }
      return { previousOrders };
    },
    onSuccess: () => {
      toast({
        title: "Statut de paiement mis à jour",
        description: "Le statut de paiement a été mis à jour avec succès.",
      });
    },
    onError: (error, variables, context) => {
      console.error('Error updating payment status:', error);
      if (context?.previousOrders) {
        queryClient.setQueryData(['adminOrders'], context.previousOrders);
      }
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour du statut de paiement.",
        variant: "destructive",
      });
    },
  });

  const deleteOrder = useMutation({
    mutationFn: async (orderId: string) => {
      console.log("Deleting order:", orderId);
      
      const { error: itemsError } = await (supabase as any)
        .from('order_items')
        .delete()
        .eq('order_id', orderId);
        
      if (itemsError) throw itemsError;
      
      const { error } = await (supabase as any)
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;
    },
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey: ['adminOrders'] });
      const previousOrders = queryClient.getQueryData(['adminOrders']) as Order[];
      queryClient.setQueryData(['adminOrders'], (old: Order[] | undefined) => {
        if (!old) return [];
        return old.filter(order => order.id !== orderId);
      });
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(null);
      }
      return { previousOrders };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast({
        title: "Commande supprimée",
        description: "La commande a été supprimée avec succès.",
      });
    },
    onError: (error, variables, context) => {
      console.error('Error deleting order:', error);
      if (context?.previousOrders) {
        queryClient.setQueryData(['adminOrders'], context.previousOrders);
      }
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la suppression de la commande.",
        variant: "destructive",
      });
    },
  });

  if (isRoleLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCcw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasOrderAccess) {
    return null;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-8">
          <h2 className="text-lg font-semibold mb-2">Erreur lors du chargement des commandes</h2>
          <p>{error.message}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['adminOrders'] })}
          >
            <RefreshCcw className="h-4 w-4 mr-2" /> Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Administration des commandes</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 bg-blue-50">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Commandes totales</p>
                <p className="text-2xl font-bold text-blue-800">{orders?.length || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-green-50">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-full">
                <PackageSearch className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Commandes en cours</p>
                <p className="text-2xl font-bold text-green-800">
                  {isLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    orders?.filter(order => 
                      ['pending', 'confirmed', 'processing'].includes(order.status)
                    ).length || 0
                  )}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-purple-50">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <RefreshCcw className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">En attente de paiement</p>
                <p className="text-2xl font-bold text-purple-800">
                  {isLoading ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    orders?.filter(order => order.payment_status === 'pending').length || 0
                  )}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {isLoading ? (
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className="p-4 space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex flex-col space-y-2">
                <Skeleton className="h-10 w-full" />
                <div className="flex space-x-2">
                  <Skeleton className="h-8 w-1/4" />
                  <Skeleton className="h-8 w-1/4" />
                  <Skeleton className="h-8 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        isMobile ? (
          <div className="space-y-4">
            {orders?.map((order) => (
              <OrderMobileCard 
                key={order.id}
                order={order}
                onSelect={() => setSelectedOrder(order)}
                onStatusChange={(status) => updateOrderStatus.mutate({ orderId: order.id, status })}
                onPaymentStatusChange={(status) => updatePaymentStatus.mutate({ orderId: order.id, status })}
                onDeleteClick={() => deleteOrder.mutate(order.id)}
              />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden border-0 shadow-lg">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Téléphone</TableHead>
                    <TableHead className="font-semibold">Adresse</TableHead>
                    <TableHead className="font-semibold">Statut</TableHead>
                    <TableHead className="font-semibold">Paiement</TableHead>
                    <TableHead className="font-semibold text-right">Total</TableHead>
                    <TableHead className="font-semibold w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders?.map((order) => (
                    <TableRow 
                      key={order.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <TableCell className="font-medium" onClick={() => setSelectedOrder(order)}>
                        {format(new Date(order.created_at), "d MMMM yyyy 'à' HH:mm", {
                          locale: fr,
                        })}
                      </TableCell>
                      <TableCell onClick={() => setSelectedOrder(order)}>{order.customer_name}</TableCell>
                      <TableCell className="text-blue-600 hover:text-blue-800" onClick={() => setSelectedOrder(order)}>
                        <a href={`mailto:${order.customer_email}`}>{order.customer_email}</a>
                      </TableCell>
                      <TableCell onClick={() => setSelectedOrder(order)}>
                        {order.customer_phone ? (
                          <a href={`tel:${order.customer_phone}`} className="text-blue-600 hover:text-blue-800">
                            {order.customer_phone}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs truncate" onClick={() => setSelectedOrder(order)}>
                        {order.shipping_address}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.status}
                          onValueChange={(value: Order['status']) => {
                            updateOrderStatus.mutate({ orderId: order.id, status: value });
                          }}
                        >
                          <SelectTrigger className={`w-[140px] border-0 ${getStatusColor(order.status)}`}>
                            <SelectValue>
                              {getStatusLabel(order.status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
                            <SelectItem value="confirmed" className="hover:bg-blue-50">Confirmée</SelectItem>
                            <SelectItem value="processing" className="hover:bg-purple-50">En traitement</SelectItem>
                            <SelectItem value="shipped" className="hover:bg-indigo-50">Expédiée</SelectItem>
                            <SelectItem value="delivered" className="hover:bg-green-50">Livrée</SelectItem>
                            <SelectItem value="cancelled" className="hover:bg-red-50">Annulée</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.payment_status}
                          onValueChange={(value: Order['payment_status']) => {
                            updatePaymentStatus.mutate({ orderId: order.id, status: value });
                          }}
                        >
                          <SelectTrigger className={`w-[140px] border-0 ${getPaymentStatusColor(order.payment_status)}`}>
                            <SelectValue>
                              {getPaymentStatusLabel(order.payment_status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
                            <SelectItem value="paid" className="hover:bg-green-50">Payée</SelectItem>
                            <SelectItem value="refunded" className="hover:bg-orange-50">Remboursée</SelectItem>
                            <SelectItem value="failed" className="hover:bg-red-50">Échec</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-medium" onClick={() => setSelectedOrder(order)}>
                        {order.total.toFixed(2)} CAD
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-600 hover:text-red-900 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Supprimer la commande</span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer la commande</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteOrder.mutate(order.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      )}

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-4xl md:w-full w-full" containerClassName="max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl flex flex-col md:flex-row md:items-center gap-2">
              Détails de la commande
              <span className="text-gray-500 text-base md:text-lg">
                #{selectedOrder?.order_number}
              </span>
            </DialogTitle>
            <DialogDescription>
              <div className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4">Informations client</h3>
                    <div className="space-y-3">
                      <p className="flex items-center text-gray-600">
                        <Package className="h-4 w-4 mr-2" />
                        {selectedOrder?.customer_name}
                      </p>
                      <p className="flex items-center text-gray-600">
                        <Mail className="h-4 w-4 mr-2" />
                        <a 
                          href={`mailto:${selectedOrder?.customer_email}`}
                          className="text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {selectedOrder?.customer_email}
                        </a>
                      </p>
                      {selectedOrder?.customer_phone && (
                        <p className="flex items-center text-gray-600">
                          <Phone className="h-4 w-4 mr-2" />
                          <a 
                            href={`tel:${selectedOrder?.customer_phone}`}
                            className="text-blue-600 hover:text-blue-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {selectedOrder?.customer_phone}
                          </a>
                        </p>
                      )}
                      <p className="flex items-start text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
                        <span className="flex-1">{selectedOrder?.shipping_address}</span>
                      </p>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <h3 className="font-semibold mb-4">Statut de la commande</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Statut</p>
                        <Select
                          value={selectedOrder?.status}
                          onValueChange={(value: Order['status']) => {
                            if (selectedOrder) {
                              updateOrderStatus.mutate({ 
                                orderId: selectedOrder.id, 
                                status: value 
                              });
                            }
                          }}
                        >
                          <SelectTrigger className={`w-full ${selectedOrder ? getStatusColor(selectedOrder.status) : ''}`}>
                            <SelectValue>
                              {selectedOrder ? getStatusLabel(selectedOrder.status) : ''}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
                            <SelectItem value="confirmed" className="hover:bg-blue-50">Confirmée</SelectItem>
                            <SelectItem value="processing" className="hover:bg-purple-50">En traitement</SelectItem>
                            <SelectItem value="shipped" className="hover:bg-indigo-50">Expédiée</SelectItem>
                            <SelectItem value="delivered" className="hover:bg-green-50">Livrée</SelectItem>
                            <SelectItem value="cancelled" className="hover:bg-red-50">Annulée</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Paiement</p>
                        <Select
                          value={selectedOrder?.payment_status}
                          onValueChange={(value: Order['payment_status']) => {
                            if (selectedOrder) {
                              updatePaymentStatus.mutate({ 
                                orderId: selectedOrder.id, 
                                status: value 
                              });
                            }
                          }}
                        >
                          <SelectTrigger className={`w-full ${selectedOrder ? getPaymentStatusColor(selectedOrder.payment_status) : ''}`}>
                            <SelectValue>
                              {selectedOrder ? getPaymentStatusLabel(selectedOrder.payment_status) : ''}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending" className="hover:bg-yellow-50">En attente</SelectItem>
                            <SelectItem value="paid" className="hover:bg-green-50">Payée</SelectItem>
                            <SelectItem value="refunded" className="hover:bg-orange-50">Remboursée</SelectItem>
                            <SelectItem value="failed" className="hover:bg-red-50">Échec</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="mt-6 p-4">
                  <h3 className="font-semibold mb-4">Articles commandés</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produit</TableHead>
                          <TableHead>Variante</TableHead>
                          <TableHead className="text-center">Quantité</TableHead>
                          <TableHead className="text-center">Prix unitaire</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder?.order_items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{item.variant_size} - {item.variant_color}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-center">{item.unit_price.toFixed(2)} CAD</TableCell>
                            <TableCell className="text-right">{item.total_price.toFixed(2)} CAD</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={4} className="text-right font-medium">Sous-total</TableCell>
                          <TableCell className="text-right">{selectedOrder?.subtotal.toFixed(2)} CAD</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} className="text-right font-medium">TPS (5%)</TableCell>
                          <TableCell className="text-right">{selectedOrder?.tps.toFixed(2)} CAD</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} className="text-right font-medium">TVQ (9.975%)</TableCell>
                          <TableCell className="text-right">{selectedOrder?.tvq.toFixed(2)} CAD</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} className="text-right font-semibold">Total</TableCell>
                          <TableCell className="text-right font-semibold">{selectedOrder?.total.toFixed(2)} CAD</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;

